<template>
  <div class="wrapper">
    <div class="text-center">
      <p class="text-h4 mt-6 mb-4 primary-color">AI</p>
    </div>

    <v-progress-circular v-if="loading" indeterminate class="d-block mx-auto"></v-progress-circular>

    <v-card v-else class="card-form mb-8">
      <v-card-title
        class="headline background-gradient embeddings-title"
        role="button"
        :aria-expanded="embeddingsOpen ? 'true' : 'false'"
        @click="embeddingsOpen = !embeddingsOpen"
      >
        <span class="headline ml-3">Embeddings</span>
        <v-spacer></v-spacer>
        <v-icon dark :class="{ 'chevron-closed': !embeddingsOpen }">mdi-chevron-down</v-icon>
      </v-card-title>
      <v-expand-transition>
      <v-card-text v-show="embeddingsOpen" class="pt-6 pl-8 pr-8">
        <p class="text-body-1">
          Provider, URL, token and model used to fill the vector tables.
          LM Studio, Ollama and OpenAI use the URL up to <span class="font-weight-medium">/v1</span>.
          The token can be empty.
        </p>
        <v-select
          v-model="form.provider"
          :items="providers"
          item-text="label"
          item-value="id"
          label="Provider"
          outlined
        ></v-select>
        <v-text-field
          v-model="form.url"
          label="URL"
          outlined
          placeholder="http://127.0.0.1:1234/v1"
        ></v-text-field>
        <v-text-field
          v-model="form.token"
          label="Token"
          outlined
          :type="showToken ? 'text' : 'password'"
          :append-icon="showToken ? 'mdi-eye-off' : 'mdi-eye'"
          @click:append="showToken = !showToken"
        ></v-text-field>
        <v-text-field
          v-model="form.model"
          label="Model"
          outlined
          placeholder="text-embedding-bge-m3"
        ></v-text-field>
        <v-alert v-if="notice" :type="noticeType" text class="mb-4">{{ notice }}</v-alert>
        <v-btn class="button-gradient-half mr-3" :disabled="saving" @click="save">Save</v-btn>
        <v-btn outlined color="#047ec3" :disabled="testing" @click="test">Test</v-btn>
      </v-card-text>
      </v-expand-transition>
    </v-card>

    <v-card v-if="!loading" class="card-form mb-8">
      <v-card-title
        class="headline background-gradient embeddings-title"
        role="button"
        :aria-expanded="chatOpen ? 'true' : 'false'"
        @click="chatOpen = !chatOpen"
      >
        <span class="headline ml-3">Chat model</span>
        <v-spacer></v-spacer>
        <v-icon dark :class="{ 'chevron-closed': !chatOpen }">mdi-chevron-down</v-icon>
      </v-card-title>
      <v-expand-transition>
      <v-card-text v-show="chatOpen" class="pt-6 pl-8 pr-8">
        <p class="text-body-1">
          Reads the CV text and proposes skills and occupations. It does not fill the vector tables.
          LM Studio, Ollama and OpenAI use the URL up to <span class="font-weight-medium">/v1</span>.
        </p>
        <v-select
          v-model="chatForm.provider"
          :items="chatProviders"
          item-text="label"
          item-value="id"
          label="Provider"
          outlined
        ></v-select>
        <v-text-field v-model="chatForm.url" label="URL" outlined placeholder="http://127.0.0.1:1234/v1"></v-text-field>
        <v-text-field
          v-model="chatForm.token"
          label="Token"
          outlined
          :type="showChatToken ? 'text' : 'password'"
          :append-icon="showChatToken ? 'mdi-eye-off' : 'mdi-eye'"
          @click:append="showChatToken = !showChatToken"
        ></v-text-field>
        <v-text-field v-model="chatForm.model" label="Model" outlined placeholder="local-model"></v-text-field>
        <v-row align="center" dense class="mb-2" justify="space-between">
          <v-col cols="12" sm="auto" class="py-0 d-flex align-center flex-grow-0">
            <v-checkbox
              v-model="chatForm.sendTemperature"
              label="Send temperature"
              hide-details
              class="mt-0 mr-4"
            ></v-checkbox>
            <v-text-field
              v-model.number="chatForm.temperature"
              label="Temperature"
              outlined
              dense
              type="number"
              step="0.1"
              min="0"
              max="2"
              hide-details
              style="max-width: 140px"
              :disabled="!chatForm.sendTemperature"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="5" md="4" class="py-0 flex-grow-0">
            <v-select
              v-model="chatForm.thinkingParam"
              :items="thinkingParamItems"
              item-text="label"
              item-value="id"
              label="Thinking param"
              outlined
              dense
              hide-details
            ></v-select>
          </v-col>
        </v-row>
        <p class="text-body-2 mb-4">
          Temperature is optional: if the checkbox is off, the request omits <span class="font-weight-medium">temperature</span>
          and the provider uses its own default. If it is on, the number beside it is sent (default 0).
        </p>
        <p class="text-body-2 mb-4">
          Thinking: choose at most one flag —
          <span class="font-weight-medium">think: false</span>
          or
          <span class="font-weight-medium">enable_thinking: false</span>
          — according to what your loaded model / runtime accepts (LM Studio, Ollama, etc.).
          Choose None to send neither. Use Test to verify the provider accepts the payload.
        </p>
        <v-alert v-if="chatNotice" :type="chatNoticeType" text class="mb-4">{{ chatNotice }}</v-alert>
        <v-btn class="button-gradient-half mr-3" :disabled="chatSaving" @click="saveChat">Save</v-btn>
        <v-btn outlined color="#047ec3" :disabled="chatTesting" @click="testChat">Test</v-btn>
      </v-card-text>
      </v-expand-transition>
    </v-card>

    <v-card v-if="!loading" class="card-form mb-8">
      <v-card-title
        class="headline background-gradient embeddings-title"
        role="button"
        :aria-expanded="tasksOpen ? 'true' : 'false'"
        @click="tasksOpen = !tasksOpen"
      >
        <span class="headline ml-3">Embedding tasks</span>
        <v-spacer></v-spacer>
        <v-icon dark :class="{ 'chevron-closed': !tasksOpen }">mdi-chevron-down</v-icon>
      </v-card-title>
      <v-expand-transition>
      <v-card-text v-show="tasksOpen" class="pt-6 pl-8 pr-8">
        <p class="text-body-1">
          Play embeds only what is missing or changed for the saved model
          <span v-if="taskModel" class="font-weight-medium">{{ taskModel }}</span>.
        </p>
        <v-simple-table>
          <thead>
            <tr>
              <th>Task</th>
              <th>Table</th>
              <th>Rows</th>
              <th>Vectors</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="task in tasks" :key="task.id">
              <td>{{ task.title }}</td>
              <td>{{ task.table }}</td>
              <td>{{ task.rows }}</td>
              <td>{{ task.stored }}</td>
              <td>
                <v-btn
                  icon
                  :aria-label="'Run ' + task.title"
                  :disabled="running"
                  @click="play(task.id, task.title)"
                >
                  <v-icon>mdi-play</v-icon>
                </v-btn>
              </td>
            </tr>
          </tbody>
        </v-simple-table>

        <div v-if="run" class="mt-6">
          <p class="text-body-1 mb-2">{{ run.title }}<span v-if="run.source"> · {{ run.source }}</span></p>
          <v-progress-linear
            :indeterminate="run.status === 'running' && (!run.total || run.phase === 'preparing')"
            :value="progressValue"
            color="#047ec3"
            height="10"
            rounded
          ></v-progress-linear>
          <p class="text-body-2 mt-3 mb-1">
            {{ progressLabel }}
          </p>
          <p class="text-body-2 mb-1">Elapsed {{ elapsedLabel }}</p>
          <p class="text-body-2 mb-3">Remaining {{ etaLabel }}</p>
          <p v-if="run.message" class="text-body-1" :class="run.status === 'error' ? 'error--text' : ''">{{ run.message }}</p>
          <v-btn v-if="run.status === 'running'" outlined color="#047ec3" class="mt-2" @click="cancelRun">Cancel</v-btn>
        </div>
      </v-card-text>
      </v-expand-transition>
    </v-card>
  </div>
</template>

<script>
export default {
  data() {
    return {
      loading: true,
      saving: false,
      testing: false,
      showToken: false,
      embeddingsOpen: true,
      chatOpen: true,
      tasksOpen: true,
      tasks: [],
      taskModel: '',
      run: null,
      now: Date.now(),
      pollTimer: null,
      clockTimer: null,
      providers: [],
      form: {
        provider: 'lmstudio',
        url: '',
        token: '',
        model: ''
      },
      notice: '',
      noticeType: 'success',
      showChatToken: false,
      chatSaving: false,
      chatTesting: false,
      chatProviders: [],
      thinkingParamItems: [
        { id: 'none', label: 'None' },
        { id: 'think', label: 'think: false' },
        { id: 'enable_thinking', label: 'enable_thinking: false' }
      ],
      chatForm: {
        provider: 'lmstudio',
        url: '',
        token: '',
        model: '',
        sendTemperature: false,
        temperature: 0,
        thinkingParam: 'none'
      },
      chatNotice: '',
      chatNoticeType: 'success'
    }
  },
  computed: {
    running() {
      return this.run && this.run.status === 'running'
    },
    progressValue() {
      if (!this.run || !this.run.total) return this.run && this.run.status === 'ok' ? 100 : 0
      return Math.min(100, Math.round(100 * this.run.done / this.run.total))
    },
    progressLabel() {
      if (!this.run) return ''
      if (this.run.status === 'running' && this.run.phase === 'preparing') return 'Counting what is missing'
      if (!this.run.total) return this.run.status === 'ok' ? 'Nothing pending' : ''
      return this.run.done + ' of ' + this.run.total
    },
    elapsedLabel() {
      if (!this.run || !this.run.startedAt) return '00:00'
      const end = this.run.status === 'running' ? this.now : Date.parse(this.run.finishedAt || this.now)
      return this.formatDuration(end - Date.parse(this.run.startedAt))
    },
    etaLabel() {
      if (!this.run || this.run.status !== 'running') return '00:00'
      if (!this.run.total || this.run.done <= 0) return 'calculating'
      const elapsed = this.now - Date.parse(this.run.startedAt)
      const remain = elapsed * (this.run.total - this.run.done) / this.run.done
      return this.formatDuration(remain)
    }
  },
  mounted() {
    this.clockTimer = setInterval(() => {
      this.now = Date.now()
    }, 1000)
    this.load()
  },
  beforeDestroy() {
    clearTimeout(this.pollTimer)
    clearInterval(this.clockTimer)
  },
  methods: {
    formatDuration(ms) {
      if (!Number.isFinite(ms) || ms < 0) return '00:00'
      const total = Math.round(ms / 1000)
      const hours = Math.floor(total / 3600)
      const minutes = Math.floor((total % 3600) / 60)
      const seconds = total % 60
      const pad = (value) => String(value).padStart(2, '0')
      if (hours) return hours + ':' + pad(minutes) + ':' + pad(seconds)
      return pad(minutes) + ':' + pad(seconds)
    },
    async api(path, method, body) {
      const options = {
        method: method || 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Auth-Token': this.$cookie.get('jwt_token')
        }
      }
      if (method === 'POST') options.body = JSON.stringify(body || {})
      let response = await fetch(process.env.VUE_APP_API_ENDPOINT + path, options)
      response = await response.json()
      if (!response.success) throw new Error(response.message || 'Error')
      return response.result
    },
    async load() {
      this.loading = true
      try {
        const result = await this.api('backoffice/ai/embeddings')
        this.providers = result.providers
        this.form = {
          provider: result.settings.provider,
          url: result.settings.url,
          token: result.settings.token,
          model: result.settings.model
        }
        await this.loadTasks()
        const chat = await this.api('backoffice/ai/chat')
        this.chatProviders = chat.providers
        this.chatForm = {
          provider: chat.settings.provider,
          url: chat.settings.url,
          token: chat.settings.token,
          model: chat.settings.model,
          sendTemperature: Boolean(chat.settings.sendTemperature),
          temperature: Number.isFinite(Number(chat.settings.temperature))
            ? Number(chat.settings.temperature)
            : 0,
          thinkingParam: chat.settings.thinkingParam || 'none'
        }
      } catch (error) {
        if (error.message && error.message.includes('Authentication failed')) {
          this.$router.push('/auth-error')
          return
        }
        this.noticeType = 'error'
        this.notice = error.message
      } finally {
        this.loading = false
      }
    },
    async save() {
      this.saving = true
      this.notice = ''
      try {
        const saved = await this.api('backoffice/ai/embeddings', 'POST', this.form)
        this.form = saved
        this.noticeType = 'success'
        this.notice = 'Settings saved.'
      } catch (error) {
        this.noticeType = 'error'
        this.notice = error.message
      } finally {
        this.saving = false
      }
    },
    async test() {
      this.testing = true
      this.notice = ''
      try {
        const result = await this.api('backoffice/ai/embeddings/test', 'POST', this.form)
        this.noticeType = result.ok ? 'success' : 'warning'
        this.notice = result.ok
          ? 'The model responds with ' + result.dimensions + ' dimensions.'
          : 'It responded with ' + result.dimensions + ' dimensions. ' + result.expected + ' are required.'
      } catch (error) {
        this.noticeType = 'error'
        this.notice = error.message
      } finally {
        this.testing = false
      }
    },
    async saveChat() {
      this.chatSaving = true
      this.chatNotice = ''
      try {
        const saved = await this.api('backoffice/ai/chat', 'POST', this.chatForm)
        this.chatForm = saved
        this.chatNoticeType = 'success'
        this.chatNotice = 'Chat model saved.'
      } catch (error) {
        this.chatNoticeType = 'error'
        this.chatNotice = error.message
      } finally {
        this.chatSaving = false
      }
    },
    async testChat() {
      this.chatTesting = true
      this.chatNotice = ''
      try {
        const result = await this.api('backoffice/ai/chat/test', 'POST', this.chatForm)
        this.chatNoticeType = 'success'
        this.chatNotice = result.sample
      } catch (error) {
        this.chatNoticeType = 'error'
        this.chatNotice = error.message
      } finally {
        this.chatTesting = false
      }
    },
    async loadTasks() {
      const result = await this.api('backoffice/ai/embeddings/tasks')
      this.tasks = result.tasks
      this.taskModel = result.model
      if (result.run && (!this.run || this.run.runId === result.run.runId || result.run.status === 'running')) {
        this.run = result.run
        if (result.run.status === 'running') this.poll()
      }
    },
    async play(id) {
      this.tasksOpen = true
      try {
        this.run = await this.api('backoffice/ai/embeddings/run', 'POST', { id })
        this.now = Date.now()
        this.poll()
      } catch (error) {
        this.run = {
          status: 'error',
          title: '',
          message: error.message,
          startedAt: null,
          done: 0,
          total: 0
        }
      }
    },
    async poll() {
      if (!this.run || !this.run.runId || this.run.status !== 'running') return
      try {
        const state = await this.api('backoffice/ai/embeddings/runs/' + this.run.runId)
        if (!this.run || this.run.runId !== state.runId) return
        this.run = state
        if (state.status !== 'running') {
          await this.loadTasks()
          this.run = state
          return
        }
      } catch (error) {
        this.run = Object.assign({}, this.run, { status: 'error', message: error.message, finishedAt: new Date().toISOString() })
        return
      }
      this.pollTimer = setTimeout(() => this.poll(), 1000)
    },
    async cancelRun() {
      if (!this.run || !this.run.runId) return
      clearTimeout(this.pollTimer)
      try {
        this.run = await this.api('backoffice/ai/embeddings/runs/' + this.run.runId + '/cancel', 'POST')
        await this.loadTasks()
      } catch (error) {
        this.run = Object.assign({}, this.run, { status: 'error', message: error.message })
      }
    }
  }
}
</script>

<style scoped>
.wrapper {
  max-width: 720px;
  margin: 0 auto;
  margin-top: 40px;
}
.primary-color {
  color: #0096c7;
}
.background-gradient {
  color: white;
  box-shadow: 0 0 20px #eee;
  background-image: linear-gradient(to right, #047ec3 0%, #52ae32 200%);
}
.button-gradient-half {
  color: white;
  background-image: linear-gradient(to right, #047ec3 0%, #52ae32 100%);
}
.embeddings-title {
  cursor: pointer;
  user-select: none;
}
.embeddings-title .v-icon {
  transition: transform 0.2s ease;
}
.chevron-closed {
  transform: rotate(-90deg);
}
</style>
